import { authHeaders, authMode, login } from './auth';

/** An error returned by the API as `{ error: { code, message } }` (or a transport failure). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const API_BASE = String(import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export type QueryValue = string | number | boolean | string[] | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** Send auth headers (default true). Public routes like share links pass false. */
  auth?: boolean;
  signal?: AbortSignal;
}

/** Serialize a query object; arrays become comma lists, empty values are dropped. */
export function toQueryString(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(','));
    } else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function parseError(res: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = res.statusText || `Request failed (${res.status})`;
  try {
    const data = (await res.json()) as { error?: { code?: string; message?: string } };
    if (data?.error) {
      code = data.error.code ?? code;
      message = data.error.message ?? message;
    }
  } catch {
    /* not JSON (proxy error page etc.) */
  }
  return new ApiError(res.status, code, message);
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, signal } = opts;
  const url = `${API_BASE}/api${path}${toQueryString(query)}`;

  const send = async (forceRefresh: boolean) => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (auth) Object.assign(headers, await authHeaders(forceRefresh));
    return fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal });
  };

  let res: Response;
  try {
    res = await send(false);
    if (res.status === 401 && auth && authMode === 'cognito') {
      res = await send(true);
      if (res.status === 401) {
        await login();
      }
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network', 'Can’t reach the server. Check your connection and try again.');
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
